import type {Command, EditorView} from '@codemirror/view'

import {
  buildNextMarkerSyntax,
  buildTasklistPrefixSyntax,
  findItemPrefixes,
} from '../../../language-tools/markdown/list-utils'
import {escapeMarkdownContinuation} from '../../extensions/markdown-continuation'
import {getListSelection, remarkFollowingItems} from './utils'

/**
 * Replace the selection with a new list item, if the user is in a list. The new item will respect the current
 * list item markers and automatically increment following markers if necessary.
 */
export const insertNewListItem: Command = (view: EditorView) => {
  const selection = getListSelection(view.state)
  const firstSelectedItem = selection?.selectedItems[0]
  if (!firstSelectedItem) return false

  const {listItemPrefix, taskListPrefix} = findItemPrefixes(firstSelectedItem)
  const currentItemMarker = listItemPrefix?.raw
  if (!currentItemMarker) return false

  let nextMarker = buildNextMarkerSyntax(currentItemMarker)
  if (!nextMarker.endsWith(' ')) nextMarker += ' ' // Ensure there's a space after the marker so the user can start typing

  // If this is a task list item, add an unchecked checkbox to the new item
  if (taskListPrefix) nextMarker += buildTasklistPrefixSyntax(false)

  // Update markers for all items following the item we just inserted. We start after the last selected item in case
  // the user selected several items and replaced them all at once with a new item
  const lastSelectedItem = selection.selectedItems.at(-1)!
  const additionalChanges = selection.list.ordered
    ? remarkFollowingItems(selection.list, selection.list.children.indexOf(lastSelectedItem), nextMarker)
    : []

  view.dispatch(view.state.replaceSelection(`\n${nextMarker}`), {
    annotations: [escapeMarkdownContinuation.of('escapeList')],
    userEvent: 'input',
    scrollIntoView: true,
    changes: additionalChanges,
  })
  return true
}
